import * as PIXI from 'pixi.js';
import Point from 'Utility/Point';


export class TilingRope extends PIXI.mesh.Rope {
    private downSample: number;
    private textureWidth: number;
    private textureHeight: number;

    constructor(texture: PIXI.Texture, points: Point[], downSample?: number) {
        super(
            texture,
            points
        );
        this.textureWidth = texture.width;
        this.textureHeight = texture.height;
        this.downSample = downSample ?? 1;
        this._refresh();
    }

    /**
     * Overrides default rope refresh to allow uvs to be tiled
     */
    protected _refresh() {
        const points = this.points;
        if (!points || points.length <= 1 || this.textureWidth === undefined)
            return;

        // if the number of points has changed we will need to recreate the arraybuffers
        if (this.vertices.length / 4 !== points.length) {
            this.vertices = new Float32Array(points.length * 4);
            this.uvs = new Float32Array(points.length * 4);
            this.colors = new Array<number>(points.length * 2);
            this.indices = new Uint16Array(points.length * 2);
        }

        const uvs = this.uvs;
        const indices = this.indices;
        const colors = this.colors;

        let uvXStart = 0;
        let lastPoint = points[0];
        const textureWidth = this.textureWidth / this.downSample;
        const total = points.length;

        // UVs
        for (let i = 0; i < total; i++) {
            // Get index start points for current iteration
            const uvIndex = i * 4;
            const colorIndex = i * 2;
            const indicesStartIndex = i * 2;

            // Get current point and the distance from the previous point
            const point = points[i];
            const dx = lastPoint.x - point.x;
            const dy = lastPoint.y - point.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            // Tile by incrementing the uvXStart by the % of the texture that is covered in the distance between the 2 points
            const factor = dist / textureWidth;
            uvXStart += factor;
            lastPoint = point;

            // Set uvs
            uvs[uvIndex] = uvXStart;
            uvs[uvIndex + 1] = 0;
            uvs[uvIndex + 2] = uvXStart;
            uvs[uvIndex + 3] = 1;

            // Set color
            colors[colorIndex] = 1;
            colors[colorIndex + 1] = 1;

            // Set indices
            indices[indicesStartIndex] = indicesStartIndex;
            indices[indicesStartIndex + 1] = indicesStartIndex + 1;
        }

        // Set to dirty to insure update
        this.dirty++;
        this.indexDirty++;

        this.multiplyUvs();
        this.refreshVertices();
    }

    /**
     * Overrides default rope refreshVertices to allow for downSampling
     */
    refreshVertices() {
        const points = this.points;

        if (points.length < 1)
            return;

        let lastPoint = points[0];
        let nextPoint;
        let perpX = 0;
        let perpY = 0;

        const vertices = this.vertices;
        const total = points.length;

        for (let i = 0; i < total; i++) {
            const point = points[i];
            const index = i * 4;

            if (i < points.length - 1) {
                nextPoint = points[i + 1];
            } else {
                nextPoint = point;
            }

            perpY = -(nextPoint.x - lastPoint.x);
            perpX = nextPoint.y - lastPoint.y;

            let ratio = (1 - i / (total - 1)) * 10;
            if (ratio > 1) {
                ratio = 1;
            }

            const perpLength = Math.sqrt(perpX * perpX + perpY * perpY);
            // Apply downSampling
            const num = (this.textureHeight * 0.5) / this.downSample;

            perpX /= perpLength;
            perpY /= perpLength;
            perpX *= num;
            perpY *= num;

            vertices[index] = point.x + perpX;
            vertices[index + 1] = point.y + perpY;
            vertices[index + 2] = point.x - perpX;
            vertices[index + 3] = point.y - perpY;

            lastPoint = point;
        }
    }

    /**
     * Overrides default rope update to be sure we update the uvs as well as the vertices on update
     */
    update() {
        if (this.autoUpdate) {
            this._refresh();
        }
        this.containerUpdateTransform();
    }
}
